import type { KeyboardEvent } from 'react'
import React from 'react'
import { FocusScope, useFocusManager } from '@react-aria/focus'
import classNames from 'classnames'

import type { CardProps } from '../Card/Card'
import { Card } from '../Card/Card'
import { POPOVER_AVAILABLE_HEIGHT_PROPERTY } from '../Popover/popoverConstants'
import type { MenuSize } from './constants'
import { MenuProvider, useMenuContext } from './MenuProvider'

export type MenuProps = {
  children: React.ReactNode
  size?: MenuSize
  width?: number | string
  'data-test-id'?: string
  className?: string
  style?: React.CSSProperties
  onRowClick?: (id: string) => void
  autoFocus?: boolean
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void
  containFocus?: boolean
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'contentEditable'> &
  CardProps

export function Menu({
  children,
  width = 320,
  size = 'medium',
  'data-test-id': dataTestId,
  className,
  style,
  autoFocus = false,
  containFocus = false,
  onRowClick,
  onKeyDown,
  ref,
  ...props
}: MenuProps) {
  const focusManager = useFocusManager()
  const { parentPosition } = useMenuContext()
  const handleNavigationKeys = React.useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      onKeyDown?.(event)
      if (event.defaultPrevented) {
        return
      }

      if (event.key === 'ArrowUp') {
        focusManager?.focusPrevious()
        return
      }

      if (event.key === 'ArrowDown') {
        focusManager?.focusNext()
      }
    },
    [focusManager, onKeyDown]
  )

  return (
    <MenuProvider value={{ onRowClick, size, parentPosition }}>
      <Card
        ref={ref}
        data-test-id={dataTestId}
        className={classNames(
          'overflow-y-auto animation-slide-in-from-top padding-vertical-4',
          className
        )}
        style={{
          width,
          maxHeight: `var(${POPOVER_AVAILABLE_HEIGHT_PROPERTY})`,
          ...style,
        }}
        onKeyDown={handleNavigationKeys}
        {...props}
      >
        <FocusScope contain={containFocus} autoFocus={autoFocus} restoreFocus>
          <div className="flexbox flexbox-column flexbox-align-stretch flexbox-row-gap-2">
            {children}
          </div>

          {/* Bug: <FocusScope autoFocus> requires at least one element to place the focus on.
          Workaround: the following <div> ensures there's at least one element.
          Can be removed when https://github.com/adobe/react-spectrum/issues/4186 is fixed */}
          <div></div>
        </FocusScope>
      </Card>
    </MenuProvider>
  )
}
